import DefaultAwardIcon from '@assets/icons/DefaultAwardIcon'
import DelegationBanner from '@components/DelegationBanner'
import { useDialog } from '@globals/dialog/useDialogStore'
import { AwardType, MediaObjectUrl } from '@kudos/http-client'
import Box from '@mui/material/Box'
import EngagementDialogWithMobileDrawer from '@shared/dialog/EngagementDialogWithMobileDrawer'
import { useTranslatedField } from '@shared/utils/useTranslatedField'
import { FC } from 'react'
import { useShallow } from 'zustand/shallow'
import { AwardsRouteContext, AwardsRouteContextType } from '../AwardsRouteContext'
import AwardTypePointsChip from '../sendAward/AwardTypePointsChip'
import SendAwardForm from './SendAwardForm'
import { useSendAwardFormStore } from './useSendAwardFormStore'

export type SendAwardsDialogData = {
  awardType: AwardType
  delegatorId?: string
  delegatorFirstName?: string
  delegatorLastName?: string
  delegatorAvatarUrls?: MediaObjectUrl | null
}

export const SEND_AWARDS_MODAL_KEY = 'sendAwardsModalKey'
const SendAwardsModal: FC = () => {
  const { dialogData } = useDialog<SendAwardsDialogData>(SEND_AWARDS_MODAL_KEY)
  const { isFormDirty } = useSendAwardFormStore(
    useShallow(state => ({
      isFormDirty: state.isFormDirty,
    })),
  )
  const getTranslatedField = useTranslatedField()
  const translatedName = getTranslatedField(dialogData?.awardType, 'name')

  const award = dialogData?.awardType
  const delegatorId = dialogData?.delegatorId
  const delegatorFirstName = dialogData?.delegatorFirstName
  const delegatorLastName = dialogData?.delegatorLastName
  const delegatorAvatarUrls = dialogData?.delegatorAvatarUrls

  // Create context value with delegator info for child components
  const contextValue: AwardsRouteContextType = {
    awardTypesData: [],
    isLoading: false,
    isFetchingNextPage: false,
    fetchNextPage: () => {
      // No-op: Modal doesn't paginate
    },
    hasNextPage: false,
    delegatorId,
    delegatorFirstName,
    delegatorLastName,
    delegatorAvatarUrls,
  }

  return (
    award && (
      <AwardsRouteContext.Provider value={contextValue}>
        <EngagementDialogWithMobileDrawer
          dialogKey={SEND_AWARDS_MODAL_KEY}
          isFormDirty={isFormDirty}
          avatarUrls={award.attachmentUrls}
          defaultIcon={<DefaultAwardIcon />}
          chip={
            <AwardTypePointsChip
              points={award.points}
              pointsLocked={award.pointsLocked}
              isNomination={false}
            />
          }
          title={translatedName}>
          {!!delegatorId && (
            <Box sx={{ mx: -3, mt: -1.5 }}>
              <DelegationBanner
                delegatorId={delegatorId}
                delegatorFirstName={delegatorFirstName}
                delegatorLastName={delegatorLastName}
                delegatorAvatarUrls={delegatorAvatarUrls}
              />
            </Box>
          )}
          <SendAwardForm awardType={award} />
        </EngagementDialogWithMobileDrawer>
      </AwardsRouteContext.Provider>
    )
  )
}

export default SendAwardsModal
